import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { runThemeTransition } from '@/lib/motion/theme-transition'

type Theme = 'light' | 'dark' | 'system'
type Density = 'comfortable' | 'compact'

interface UiState {
  theme: Theme
  density: Density
  colorblind: boolean
  sidebarCollapsed: boolean
  /**
   * The SHORTCUTS "+" picker's saved choice (features/shortcuts), per device
   * — `null` until the member saves it once, so a deliberate empty save
   * stays empty rather than reading as "never touched" (see
   * `usePinnedToolIds`). No preferences endpoint exists yet
   * (`/api/v5/auth/me/` is GET-only), so this cannot sync across devices —
   * BACKEND CHANGES NEEDED (CRS-MR-3).
   */
  pinnedTools: readonly string[] | null
  /**
   * Account › Privacy's "Hide balances": figures open masked (Rs ••••) until the member clicks the
   * eye, which reveals them for that visit only. Per device, as the theme is.
   */
  hideBalances: boolean
  setTheme: (t: Theme) => void
  setDensity: (d: Density) => void
  setColorblind: (on: boolean) => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setPinnedTools: (ids: readonly string[]) => void
  setHideBalances: (on: boolean) => void
}

const prefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches

/**
 * The Flutter app's theme, while the page runs inside it (lib/embed). It wins
 * over the member's own setting and is never persisted: the app owns the
 * theme there, and the member's saved choice must still be theirs in a
 * browser tab.
 */
let appTheme: 'light' | 'dark' | null = null

/** Applies presentation state to <html>. Components never read the theme. */
const resolvesDark = (theme: Theme) => {
  const shown = appTheme ?? theme
  return shown === 'dark' || (shown === 'system' && prefersDark())
}

const applyToDocument = (state: Pick<UiState, 'theme' | 'density' | 'colorblind'>) => {
  const root = document.documentElement
  root.classList.toggle('dark', resolvesDark(state.theme))
  root.classList.toggle('cb', state.colorblind)
  root.dataset['density'] = state.density
}

/**
 * The only function that writes 'mm-theme'. index.html's pre-paint script
 * needs the theme before `persist`'s rehydration — before any JS module has
 * even loaded — so it reads this plain key instead of parsing the 'mm-ui'
 * blob. Two call sites reach it — `setTheme` and `initUi`, see below — but
 * both always write the SAME thing: whatever `useUiStore`'s current theme
 * is, right now. That is what keeps them from becoming two disagreeing
 * writers the way the old code was: the old `initUi` wrote a value once at
 * boot that could later go stale when `setTheme` changed the store without
 * persisting anything. Here, every write is a fresh read of the live store,
 * so there is no snapshot to go stale — 'mm-theme' can never say anything
 * other than "what the store believes right now".
 *
 * Stores the setting itself ('system' included), never a resolved
 * 'dark'/'light' — index.html re-derives 'system' from matchMedia on every
 * load rather than trusting what the OS said at write time, which is the
 * other half of the same staleness bug for a member who leaves the tab
 * closed while their OS theme changes.
 */
const persistThemeKey = (theme: Theme) => {
  try {
    localStorage.setItem('mm-theme', theme)
  } catch {
    /* private mode — the in-page classes set by applyToDocument still hold */
  }
}

/** The one path for a live theme change; boot never goes through it. */
const swapTheme = (dark: boolean, apply: () => void) => {
  if (dark === document.documentElement.classList.contains('dark')) {
    apply()
    return
  }
  runThemeTransition(apply)
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      density: 'comfortable',
      colorblind: false,
      sidebarCollapsed: false,
      pinnedTools: null,
      hideBalances: false,

      setTheme: (theme) => {
        persistThemeKey(theme)
        swapTheme(resolvesDark(theme), () => {
          set({ theme })
          applyToDocument({ ...get(), theme })
        })
      },
      setDensity: (density) => {
        set({ density })
        applyToDocument({ ...get(), density })
      },
      setColorblind: (colorblind) => {
        set({ colorblind })
        applyToDocument({ ...get(), colorblind })
      },
      // The rail's width is laid out by the chrome, not by a class on <html>,
      // so this is the one piece of UI state that does not reach the document.
      setSidebarCollapsed: (sidebarCollapsed) => {
        set({ sidebarCollapsed })
      },
      setPinnedTools: (pinnedTools) => {
        set({ pinnedTools })
      },
      setHideBalances: (hideBalances) => {
        set({ hideBalances })
      },
    }),
    {
      name: 'mm-ui',
      onRehydrateStorage: () => (state) => {
        if (state) applyToDocument(state)
      },
    },
  ),
)

/**
 * A live change of the Flutter app's theme (see `appTheme`), animated as
 * `setTheme` is. The one it opened the page with goes through `initUi`.
 */
export const setAppTheme = (theme: 'light' | 'dark'): void => {
  swapTheme(theme === 'dark', () => {
    appTheme = theme
    applyToDocument(useUiStore.getState())
  })
}

/**
 * Called once at boot, after rehydration, and on OS theme changes.
 *
 * `persist`'s rehydration for 'mm-ui' runs synchronously against
 * localStorage during `useUiStore`'s own module-level `create()` call —
 * zustand's `hydrate()` resolves `storage.getItem` through a custom
 * thenable that calls its `.then` handler inline rather than via a native
 * Promise's microtask, so for localStorage (never a Promise-returning
 * storage) the whole rehydration chain, including `set(stateFromStorage,
 * true)`, completes before `create()` returns. Since `useUiStore` is
 * imported (and thus created) before this function is ever called,
 * `useUiStore.getState()` below already reflects the rehydrated 'mm-ui',
 * not the store's hardcoded defaults.
 *
 * That ordering is what makes this write safe. Every member who used the
 * app before `setTheme` became 'mm-theme's sole writer has a RESOLVED
 * 'dark'/'light' sitting in that key from the old `initUi`, even if their
 * actual setting is 'system' — nothing else in this codebase ever
 * corrects it, so they get a wrong-theme flash on every load until they
 * happen to re-pick a theme. Re-persisting the (now rehydrated, correct)
 * setting here fixes that on their very next load, and does not
 * reintroduce a second, independent writer: this always writes the SAME
 * live value `setTheme` would, derived the same way, so the two can never
 * disagree — see `persistThemeKey`'s own comment.
 */
export const initUi = (openedWith: { appTheme: 'light' | 'dark' | null } = { appTheme: null }) => {
  // Set before this apply, still in the boot task, so the first painted frame
  // keeps the theme index.html's pre-paint script gave it.
  appTheme = openedWith.appTheme
  applyToDocument(useUiStore.getState())
  persistThemeKey(useUiStore.getState().theme)

  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  mq.addEventListener('change', () => {
    const state = useUiStore.getState()
    if (state.theme === 'system') {
      swapTheme(resolvesDark('system'), () => {
        applyToDocument(state)
      })
    }
  })
}
